<template>
  <div class="lesson-preview-container">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :model-value="isLoading"
      message="Loading Lesson..."
      :full-screen="true"
      :teleport-to-body="true"
    />

    <!-- Error State -->
    <div v-if="!isLoading && error" class="error-state">
      <div class="error-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2>Failed to Load Lesson</h2>
        <p>{{ error }}</p>
        <button class="back-button" @click="goBack">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Editor
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-if="!isLoading && !error && lessonData.title"
      class="lesson-content-wrapper"
    >
      <div class="lesson-main-content">
        <div class="lesson-header">
          <div class="lesson-header-content">
            <div class="lesson-navigation-container">
              <ul class="lesson-navigation-list">
                <li>
                  <a @click="goBack" style="cursor: pointer"> Lesson Editor </a>
                </li>
                <li>/</li>
                <li class="lesson-navigation-title">
                  {{ lessonData.title }}
                </li>
              </ul>
            </div>
            <div class="lesson-title-container">
              <h1 class="lesson-title">
                {{ lessonData.title || "Untitled Lesson" }}
              </h1>
              <div class="lesson-meta">
                <div class="lesson-meta-item">
                  <div class="lesson-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <span>{{ lessonData.duration || "0" }} minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="lesson-main-container">
          <div class="lesson-content" :class="{ expanded: sidebarCollapsed }">
            <div class="lesson-tabs">
              <div
                v-for="(tab, index) in lessonData.tabs"
                :key="index"
                class="lesson-tab"
                :class="{ active: activeTabIndex === index }"
                @click="activeTabIndex = index"
              >
                {{ tab.name }}
                <div
                  v-if="tab.duration && activeTabIndex === index"
                  class="tab-duration"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>{{ tab.duration }} Minutes</span>
                </div>
              </div>
            </div>

            <div
              class="lesson-tab-content"
              ref="lessonTabContentRef"
              v-if="lessonData.tabs && lessonData.tabs.length > 0"
            >
              <template v-if="currentTab.type === 'assessment'">
                <AssessmentView :assessments="currentTab.assessments" />
              </template>
              <template v-else>
                <div
                  v-for="(section, sectionIndex) in getCurrentTabSections()"
                  :key="sectionIndex"
                  class="lesson-section"
                >
                  <div class="tiptap-content" v-html="section.content"></div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="
            (lessonData.slide_url ||
              lessonData.lesson_instructional?.length > 0) &&
            activeTabIndex === 0
          "
          style="
            display: flex;
            gap: 10px;
            flex-direction: column;
            width: fit-content;
            align-items: flex-start;
          "
        >
          <h2>Resources</h2>
          <v-btn
            v-if="lessonData.slide_url"
            class="custom-button"
            color="#1E3A5F"
            size="large"
            style="
              border-radius: 15px;
              text-transform: none;
              font-size: var(--fs-big);
            "
            @click="openCopySlideModal"
          >
            <v-icon start>mdi-content-copy</v-icon>
            <span>Copy <strong>Slide Deck</strong> to your Google Drive</span>
          </v-btn>
          <v-btn
            v-for="material in lessonData.lesson_instructional"
            :key="material.id || material.name"
            class="custom-button"
            color="#1E3A5F"
            size="large"
            style="
              border-radius: 15px;
              text-transform: none;
              font-size: var(--fs-big);
            "
            @click="openCopyResourceModal(material)"
          >
            <v-icon start>mdi-content-copy</v-icon>
            <span
              >Copy <strong>{{ material.name }}</strong> to your Google
              Drive</span
            >
          </v-btn>
        </div>
      </div>
      <div>
        <ViewLessonSidebar
          :categories="categories"
          v-model:collapsed="sidebarCollapsed"
        />
      </div>
    </div>

    <div v-if="!isLoading && !error" class="action-buttons">
      <button class="back-button" @click="goBack">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back to Editor
      </button>
    </div>

    <CopySlideDeck
      :is-open="isCopySlideModalOpen"
      :file-url="currentCopyResource.url"
      :file-name="currentCopyResource.name"
      @update:is-open="isCopySlideModalOpen = $event"
    />
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  computed,
  nextTick,
  watch,
  onBeforeUnmount,
  defineProps,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { setupSlideThumbnailHandlers } from "@/tiptap-extensions/slide-deck/slideModalHelper";
import AssessmentView from "@/components/lesson-library/AssessmentView.vue";
import ViewLessonSidebar from "@/components/lesson-library/ViewLessonSidebar.vue";
import CopySlideDeck from "@/components/lesson-library/CopySlideDeck.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import lessonPlanService from "@/services/lessonPlanService";

defineProps({
  id: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const activeTabIndex = ref(0);
const sidebarCollapsed = ref(false);
const lessonTabContentRef = ref(null);
const isCopySlideModalOpen = ref(false);
const currentCopyResource = ref({ url: "", name: "" });
const isLoading = ref(true);
const error = ref("");

// Categories for sidebar
const categories = ref([]);

// Default lesson data structure
const defaultLessonShell = {
  title: null,
  subtitle: null,
  duration: null,
  slide_url: "",
  lesson_instructional: [],
  tabs: [],
};

const lessonData = ref(JSON.parse(JSON.stringify(defaultLessonShell)));

// Move function declarations to the top level
function initAccordions(container) {
  if (!container) return;
  const tooltipBlocks = container.querySelectorAll(".tooltip-block");
  tooltipBlocks.forEach((block) => {
    block.classList.remove("is-open");
    block.removeAttribute("data-open");
    const titleLine = block.querySelector(".tooltip-title-line");
    const bodyWrapper = block.querySelector(".tooltip-body-wrapper");
    const arrowEl = block.querySelector(".accordion-arrow");
    if (titleLine && bodyWrapper) {
      const titleEl = block.querySelector(".tooltip-title");
      const bodyEl = block.querySelector(".tooltip-body");
      if (titleEl) titleEl.setAttribute("contenteditable", "false");
      if (bodyEl) bodyEl.setAttribute("contenteditable", "false");

      const existingHandler = titleLine.__accordionClickHandler__;
      if (existingHandler) {
        titleLine.removeEventListener("click", existingHandler);
      }

      const toggleAccordion = () => {
        const isOpen = block.classList.contains("is-open");
        if (isOpen) {
          block.classList.remove("is-open");
          bodyWrapper.classList.remove("is-open");
          if (arrowEl) arrowEl.classList.remove("is-open");
          block.removeAttribute("data-open");
        } else {
          block.classList.add("is-open");
          bodyWrapper.classList.add("is-open");
          if (arrowEl) arrowEl.classList.add("is-open");
          block.setAttribute("data-open", "");
        }
      };
      titleLine.addEventListener("click", toggleAccordion);
      titleLine.__accordionClickHandler__ = toggleAccordion;
    }
  });
}

let domObserver = null;

const runDomObserver = () => {
  const contentArea = lessonTabContentRef.value;
  if (contentArea) {
    initAccordions(contentArea);
    setupSlideThumbnailHandlers(contentArea);

    if (typeof MutationObserver !== "undefined") {
      domObserver = new MutationObserver((mutationsList) => {
        let needsAccordionInit = false;
        let needsSlideHandlerInit = false;

        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            for (const node of Array.from(mutation.addedNodes).concat(
              Array.from(mutation.removedNodes)
            )) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (
                  node.matches(".tooltip-block") ||
                  node.querySelector(".tooltip-block")
                ) {
                  needsAccordionInit = true;
                }
                if (
                  node.matches(".slide-deck-container") ||
                  node.querySelector(".slide-deck-container")
                ) {
                  needsSlideHandlerInit = true;
                }
              }
            }
          }
        }

        if (needsAccordionInit || needsSlideHandlerInit) {
          nextTick(() => {
            if (needsAccordionInit && lessonTabContentRef.value)
              initAccordions(lessonTabContentRef.value);
            if (needsSlideHandlerInit && lessonTabContentRef.value)
              setupSlideThumbnailHandlers(lessonTabContentRef.value);
          });
        }
      });

      domObserver.observe(contentArea, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }
};

const currentTab = computed(() => {
  if (
    !lessonData.value.tabs ||
    activeTabIndex.value >= lessonData.value.tabs.length
  ) {
    return { type: null, assessments: [], sections: [] };
  }
  return lessonData.value.tabs[activeTabIndex.value];
});

const getCurrentTabSections = () => {
  if (
    lessonData.value.tabs &&
    lessonData.value.tabs.length > 0 &&
    activeTabIndex.value < lessonData.value.tabs.length
  ) {
    return lessonData.value.tabs[activeTabIndex.value].sections || [];
  }
  return [];
};

const openCopySlideModal = () => {
  currentCopyResource.value = {
    url: lessonData.value.slide_url,
    name: "Slide Deck",
  };
  isCopySlideModalOpen.value = true;
};

const openCopyResourceModal = (material) => {
  currentCopyResource.value = {
    url: material.url,
    name: material.name,
  };
  isCopySlideModalOpen.value = true;
};

const loadLessonData = async () => {
  const lessonId = route.params.id;

  if (!lessonId) {
    error.value = "No lesson ID provided";
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    error.value = "";

    const response = await lessonPlanService.getLessonById(lessonId);

    if (response.data && response.data.data) {
      const apiData = response.data.data;

      lessonData.value.id = apiData.id;
      lessonData.value.title = apiData.title;
      lessonData.value.subtitle = apiData.short_description;
      lessonData.value.duration = apiData.duration;
      lessonData.value.slide_url = apiData.slide_url;
      lessonData.value.lesson_instructional =
        apiData.lesson_instructional || [];

      // Parse and set lesson content
      const contents = JSON.parse(apiData.content || "[]");
      if (contents && contents.length > 0) {
        lessonData.value.tabs = contents;
      }

      // Set categories for sidebar
      categories.value = apiData.categories || [];

      // Initialize DOM observer after content is loaded
      nextTick(() => {
        if (lessonTabContentRef.value) {
          runDomObserver();
        }
      });
    } else {
      throw new Error("Invalid response format");
    }
  } catch (err) {
    console.error("Error loading lesson:", err);
    error.value =
      err.response?.data?.message || "Failed to load lesson. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadLessonData();
});

onBeforeUnmount(() => {
  if (domObserver) {
    domObserver.disconnect();
  }
  if (lessonTabContentRef.value) {
    const tooltipBlocks = lessonTabContentRef.value.querySelectorAll(
      ".tooltip-title-line"
    );
    tooltipBlocks.forEach((titleLine) => {
      if (titleLine.__accordionClickHandler__) {
        titleLine.removeEventListener(
          "click",
          titleLine.__accordionClickHandler__
        );
        delete titleLine.__accordionClickHandler__;
      }
    });
  }
});

const goBack = () => {
  router.push({ name: "LessonPlansEditPage", params: { id: route.params.id } });
};

watch(activeTabIndex, () => {
  nextTick(() => {
    if (lessonTabContentRef.value) {
      initAccordions(lessonTabContentRef.value);
      setupSlideThumbnailHandlers(lessonTabContentRef.value);
    }
  });
});

// Watch for route changes
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      activeTabIndex.value = 0;
      loadLessonData();
    }
  }
);
</script>

<style scoped>
.lesson-preview-container {
  margin: 0 auto;
  padding: 35px 0 35px 35px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
}

.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.error-content {
  text-align: center;
  color: #666;
}

.error-content svg {
  color: #dc3545;
  margin-bottom: 20px;
}

.error-content h2 {
  margin: 0 0 10px 0;
  color: #333;
}

.error-content p {
  margin: 0 0 20px 0;
  color: #666;
}

.lesson-content-wrapper {
  display: flex;
}

.lesson-main-content {
  width: 100%;
}

.lesson-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
  margin-bottom: 20px;
}

.lesson-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
}

.lesson-navigation-container {
  display: flex;
  align-items: center;
}

.lesson-navigation-list {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  margin: 0;
}

.lesson-navigation-list li {
  font-size: 0.9rem;
  list-style: none;
}

.lesson-navigation-list li a {
  text-decoration: none;
  color: #333;
}

.lesson-navigation-title {
  color: var(--primary-pink);
  font-weight: 600;
}

.lesson-title-container {
  width: 100%;
}

.lesson-meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-blue);
}

.lesson-meta-item-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lesson-title {
  font-size: 2.5em;
  margin: 0 0 10px 0;
  color: #333;
}

.lesson-meta {
  display: flex;
  gap: 20px;
  color: #777;
  align-items: center;
  margin-top: 40px;
}

.lesson-main-container {
  display: flex;
  position: relative;
  flex: 1;
}

.lesson-content {
  flex: 1;
  transition: all 0.3s ease;
  border-radius: 8px;
  background-color: #fff;
  margin-right: 20px;
}

.lesson-content.expanded {
  margin-right: 50px;
}

.lesson-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 40px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
}

.lesson-tab {
  padding: 8px 20px;
  cursor: pointer;
  color: #777;
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
  border-bottom: 3px solid transparent;
}

.lesson-tab:hover {
  color: var(--primary-pink, #ff5a5f);
  border-bottom-color: var(--primary-pink, #ff5a5f);
}

.lesson-tab.active {
  border-bottom: 3px solid var(--primary-pink, #ff5a5f);
  color: var(--primary-pink, #ff5a5f);
  font-weight: bold;
}

.tab-duration {
  font-size: 0.8em;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -30px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.lesson-tab-content {
  min-height: 300px;
}

.lesson-section {
  margin-bottom: 30px;
}

/* Content styles are now handled globally in tiptap-editor.css */
.tiptap-content h2 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #333;
  font-weight: 500;
}

.back-button:hover {
  background-color: #e0e0e0;
}

@media (max-width: 768px) {
  .lesson-header-content {
    flex-direction: column;
  }

  .lesson-main-container {
    flex-direction: column-reverse;
  }

  .lesson-content {
    width: 100%;
    margin-right: 0;
  }

  .lesson-tabs {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 10px;
  }

  .lesson-tab {
    display: inline-block;
  }
}
</style>

<!-- Global styles for rendered content -->
<style>
/* Slide deck styles */
.slide-deck-container {
  margin: 1.5rem 0;
  border-radius: 8px;
  background-color: var(--white-hover-1);
}

.slide-deck-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 8px;
  align-items: flex-start;
}

.slide-deck-thumbnail {
  flex-shrink: 0;
  width: 469px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.slide-deck-thumbnail:hover {
  opacity: 0.9;
}

.slide-deck-thumbnail img {
  width: 100%;
  height: 265px;
  object-fit: cover;
  border-radius: 0.75rem;
  transition: transform 0.2s ease;
}

.slide-deck-thumbnail:hover img {
  transform: scale(1.03);
}

.slide-deck-title {
  font-weight: 500;
  color: var(--black-body-text);
  margin-top: 0.5rem;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
}

.slide-deck-info {
  flex: 1 0 0;
  min-width: 1px;
  padding-left: 16px;
  border-left: 2px solid var(--neutral-100);
  align-self: center;
}

.slide-deck-notes {
  color: var(--black-body-text);
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.15px;
  font-family: "League Spartan", sans-serif;
  min-height: 50px;
}

@media (max-width: 768px) {
  .slide-deck-content {
    flex-direction: column;
  }

  .slide-deck-thumbnail {
    width: 100%;
  }

  .slide-deck-thumbnail img {
    height: auto;
    aspect-ratio: 469 / 265;
  }

  .slide-deck-info {
    border-left: none;
    padding-left: 0;
  }

  .slide-deck-notes {
    font-size: var(--fs-small);
    line-height: 20px;
    text-align: left;
  }
}
</style>
